import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check, ChevronDown } from "lucide-react";
import { SUPPORTED_LANGS } from "@/i18n";

/**
 * Site-wide language switcher — flips i18next locale AND Google Translate
 * so 100% of the page (including content i18n hasn't captured yet) translates.
 */

// Set the googtrans cookie on the root path AND host so Google Translate picks
// it up on next render. Reload forces GT to re-parse the DOM into the new lang.
function setGoogTransCookie(target) {
  const value = target === "en" ? "" : `/en/${target}`;
  const host = window.location.hostname;
  const domains = ["", host, "." + host];
  // Clear any existing googtrans across scopes first
  domains.forEach((d) => {
    document.cookie = `googtrans=; path=/; ${d ? `domain=${d};` : ""} expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  });
  if (value) {
    document.cookie = `googtrans=${value}; path=/;`;
    document.cookie = `googtrans=${value}; path=/; domain=${host};`;
    document.cookie = `googtrans=${value}; path=/; domain=.${host};`;
  }
}

export default function SiteLanguageSwitcher({ compact = false }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const currentCode = (i18n.language || "en").split("-")[0].toLowerCase();
  const current = SUPPORTED_LANGS.find((l) => l.code === currentCode) || SUPPORTED_LANGS[0];

  const select = (code) => {
    i18n.changeLanguage(code);
    setGoogTransCookie(code);
    setOpen(false);
    // Reload to let Google Translate re-render every text node
    setTimeout(() => window.location.reload(), 100);
  };

  return (
    <div className="relative" data-testid="site-lang-switcher">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="site-lang-trigger"
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-zinc-700 hover:text-zinc-900 border border-transparent hover:border-zinc-200 transition-colors notranslate"
      >
        {compact ? (
          <Globe className="w-4 h-4" />
        ) : (
          <>
            <span className="text-base leading-none">{current.flag}</span>
            <span className="font-mono uppercase tracking-wider">{current.label}</span>
          </>
        )}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <button aria-label="Close language menu" className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <ul
            role="listbox"
            data-testid="site-lang-menu"
            className="absolute right-0 top-full mt-1 z-50 min-w-[190px] bg-white border border-zinc-200 shadow-lg py-1 notranslate"
          >
            {SUPPORTED_LANGS.map((l) => {
              const active = l.code === currentCode;
              return (
                <li key={l.code}>
                  <button
                    type="button"
                    onClick={() => select(l.code)}
                    data-testid={`site-lang-option-${l.code}`}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${
                      active ? "bg-zinc-50 text-zinc-900 font-medium" : "text-zinc-700 hover:bg-zinc-50"
                    }`}
                  >
                    <span className="text-base leading-none">{l.flag}</span>
                    <span className="flex-1">{l.native}</span>
                    <span className="text-[10px] font-mono uppercase text-zinc-400">{l.label}</span>
                    {active && <Check className="w-3.5 h-3.5 text-emerald-600" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
